import { ErrorResponse } from '@aibees/shared/definition';
import { UseQueryResult } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import useCompanySearchItemsStore from '../store/company-search-items-store';
import { CompanySearchResponse } from './types';

export function useUpdateCompanySearchItemsStore(
  query: UseQueryResult<CompanySearchResponse, ErrorResponse>,
  isNotSearchTableQuery?: boolean
) {
  const { setCompanySearchItems } = useCompanySearchItemsStore();

  const memoizedData = useMemo(() => {
    if (!query.data?.content) return null;
    return {
      content: query.data.content,
      top: query.data.top,
      // Include other relevant fields that affect store updates
    };
  }, [query.data?.content, query.data?.top]); // Only when actual content changes

  useEffect(() => {
    if (!memoizedData || isNotSearchTableQuery) return;

    setCompanySearchItems((currentItems) => {
      const existingIds = currentItems.map((item) => item.id);
      const newIds = memoizedData.content.map((item) => item.id);

      // Check if data has changed
      const isIdsChanged =
        existingIds.length === 0 ||
        existingIds.length !== newIds.length ||
        existingIds.some((id) => !newIds.includes(id)) ||
        newIds.some((id) => !existingIds.includes(id));

      if (isIdsChanged) {
        // console.log('🟢 Updating store');
        const sortedTop = memoizedData.top || [];
        return [...sortedTop, ...memoizedData.content];
      }

      // console.log('🔵 No changes detected, returning same reference');
      return currentItems; // Same reference = no re-render
    });
  }, [memoizedData, isNotSearchTableQuery]);
}
